import { RoomRevenueModel, MonthRow, DealBudget } from "../../types/deal";
import { FnBState } from "../../types/fnb";
import { OpexState } from "../../types/opex";
import { PayrollState } from "../../types/payroll";
import { OtherRevenueState } from "../../types/otherRevenue";

export function getDaysInMonth(month: number): number {
  const days = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return days[month - 1] || 30;
}

export function ensure12Months(arr: number[] | undefined): number[] {
  if (!arr || !Array.isArray(arr)) return Array(12).fill(0);
  const result = arr.slice(0, 12).map((v) => Number(v) || 0);
  while (result.length < 12) result.push(0);
  return result;
}

export function safeNumber(value: any, fallback: number = 0): number {
  const num = Number(value);
  return isFinite(num) ? num : fallback;
}

export function reconstructRoomRevenueModel(
  adrArray: number[] | undefined,
  occArray: number[] | undefined,
  totalRooms: number,
  currency: string,
  seasonalityPreset?: string,
): RoomRevenueModel {
  const adr = ensure12Months(adrArray);
  const occ = ensure12Months(occArray);
  const rooms = safeNumber(totalRooms, 0);

  // Build month-by-month data with ALL computed fields
  const months: MonthRow[] = adr.map((monthAdr, i) => {
    const monthOcc = occ[i];
    const month = i + 1;
    const days = getDaysInMonth(month);
    const roomsAvailable = rooms * days;
    const roomsSold = roomsAvailable * (monthOcc / 100);
    const revpar = monthAdr * (monthOcc / 100);
    const roomsRevenue = roomsSold * monthAdr;

    return {
      month,
      adr: monthAdr,
      occPct: monthOcc,
      revpar,
      days,
      roomsAvailable,
      roomsSold,
      roomsRevenue,
    };
  });

  // Compute annual totals
  const totals = {
    roomsAvailable: months.reduce((sum, m) => sum + m.roomsAvailable, 0),
    roomsSold: months.reduce((sum, m) => sum + m.roomsSold, 0),
    roomsRevenue: months.reduce((sum, m) => sum + m.roomsRevenue, 0),
    avgADR: 0,
    avgOccPct: 0,
    avgRevPAR: 0,
  };

  // Calculate derived KPIs
  totals.avgADR =
    totals.roomsSold > 0 ? totals.roomsRevenue / totals.roomsSold : 0;
  totals.avgOccPct =
    totals.roomsAvailable > 0
      ? (totals.roomsSold / totals.roomsAvailable) * 100
      : 0;
  totals.avgRevPAR = totals.avgADR * (totals.avgOccPct / 100);

  return {
    months,
    totals,
    currency,
    seasonalityPreset,
  };
}

export function hasValidRoomRevenue(
  adrArray?: number[],
  occArray?: number[],
): boolean {
  if (!adrArray || !occArray) return false;
  return adrArray.some((v) => v > 0) || occArray.some((v) => v > 0);
}

export function reconstructFnBRevenue(fnb: any): FnBState | undefined {
  if (!fnb) return undefined;

  return {
    mode: "advanced",
    simple: {
      avgGuestsPerOccRoom: safeNumber(fnb.avgGuests, 1.5),
      totalGuestCapturePct: 225,
      avgCheckGuest: 20,
      externalCoversPerDay: 65,
      avgCheckExternal: 25,
    },
    advanced: {
      breakfast: {
        key: "breakfast",
        label: "Breakfast",
        icon: "☕",
        guestCapturePct: safeNumber(fnb.capture?.breakfast, 0),
        avgCheckGuest: safeNumber(fnb.check?.guest?.breakfast, 0),
        externalCoversPerDay: safeNumber(fnb.external?.perDay?.breakfast, 0),
        avgCheckExternal: safeNumber(fnb.check?.external?.breakfast, 0),
      },
      lunch: {
        key: "lunch",
        label: "Lunch",
        icon: "🥗",
        guestCapturePct: safeNumber(fnb.capture?.lunch, 0),
        avgCheckGuest: safeNumber(fnb.check?.guest?.lunch, 0),
        externalCoversPerDay: safeNumber(fnb.external?.perDay?.lunch, 0),
        avgCheckExternal: safeNumber(fnb.check?.external?.lunch, 0),
      },
      dinner: {
        key: "dinner",
        label: "Dinner",
        icon: "🍽️",
        guestCapturePct: safeNumber(fnb.capture?.dinner, 0),
        avgCheckGuest: safeNumber(fnb.check?.guest?.dinner, 0),
        externalCoversPerDay: safeNumber(fnb.external?.perDay?.dinner, 0),
        avgCheckExternal: safeNumber(fnb.check?.external?.dinner, 0),
      },
      bar: {
        key: "bar",
        label: "Bar",
        icon: "🍷",
        guestCapturePct: safeNumber(fnb.capture?.bar, 0),
        avgCheckGuest: safeNumber(fnb.check?.guest?.bar, 0),
        externalCoversPerDay: safeNumber(fnb.external?.perDay?.bar, 0),
        avgCheckExternal: safeNumber(fnb.check?.external?.bar, 0),
      },
    },
    distributionWeights: { breakfast: 30, lunch: 25, dinner: 35, bar: 10 },
  };
}

export function reconstructBudget(
  budget: any,
  purchasePrice: number,
): DealBudget | undefined {
  if (!budget) return undefined;

  // Extract base values
  const netPurchasePrice = safeNumber(purchasePrice, 0);
  const reTransferTax = safeNumber(budget.transferTax, 0);
  const dealCosts = safeNumber(budget.dealCosts, 0);
  const constructionCosts = safeNumber(budget.construction, 0);
  const ffeOse = safeNumber(budget.ffoeOse, 0);
  const professionalFees = safeNumber(budget.profFees, 0);
  const planningCharges = safeNumber(budget.planningFees, 0);
  const developmentFee = safeNumber(budget.devFee, 0);
  const insuranceAdmin = safeNumber(budget.insuranceAdmin, 0);
  const otherDev = safeNumber(budget.other, 0);
  const preOpening = safeNumber(budget.preopening, 0);
  const contingencyPct = safeNumber(budget.contingencyPct, 10);

  // Calculate subtotals
  const siteAcquisition = netPurchasePrice + reTransferTax + dealCosts;
  const constructionSubtotal = constructionCosts + ffeOse;
  const developmentSubtotal =
    professionalFees + planningCharges + developmentFee;
  const otherDevSubtotal = insuranceAdmin + otherDev;
  const preOpeningSubtotal = preOpening;

  // Calculate contingency and grand total
  const contingencyBase =
    siteAcquisition +
    constructionSubtotal +
    developmentSubtotal +
    otherDevSubtotal +
    preOpeningSubtotal;
  const contingencyAmount = (contingencyPct / 100) * contingencyBase;
  const grandTotal = contingencyBase + contingencyAmount;

  return {
    netPurchasePrice,
    reTransferTax,
    dealCosts,
    siteAcquisition,
    constructionCosts,
    ffeOse,
    constructionSubtotal,
    professionalFees,
    planningCharges,
    developmentFee,
    developmentSubtotal,
    insuranceAdmin,
    otherDev,
    otherDevSubtotal,
    preOpening,
    preOpeningSubtotal,
    contingencyPct,
    contingencyAmount,
    grandTotal,
  };
}

export function reconstructOpex(opex: any): OpexState | undefined {
  if (!opex) return undefined;

  return {
    cogs: safeNumber(opex.cogs, 30),
    rent: safeNumber(opex.rent, 0),
    mgmtFee: safeNumber(opex.mgmtFee, 3),
    insurance: safeNumber(opex.insurance, 0.5),
    utilities: safeNumber(opex.utilities, 3),
    otherDirect: safeNumber(opex.otherDirect, 2000),
    propertyTaxes: safeNumber(opex.propertyTaxes, 1),
    wellnessOther: safeNumber(opex.wellnessOther, 1500),
    meetingsEvents: safeNumber(opex.meetingsEvents, 2),
    roomsCommission: safeNumber(opex.roomsCommission, 15),
    maintenanceOther: safeNumber(opex.maintenanceOther, 2),
    adminGeneralOther: safeNumber(opex.adminGeneralOther, 4),
    techSubscriptions: safeNumber(opex.techSubscriptions, 1200),
    salesMarketingOther: safeNumber(opex.salesMarketingOther, 3),
    guestSuppliesCleaning: safeNumber(opex.guestSuppliesCleaning, 8),
  } as OpexState;
}

export function reconstructPayroll(payroll: any): PayrollState | undefined {
  if (!payroll) return undefined;

  // Basic simple mode reconstruction
  return {
    mode: payroll.mode || "simple",
    simple: {
      ftePerRoom: safeNumber(payroll.ftePerRoom, 0.7),
      baseReceptionSalary: safeNumber(payroll.avgSalary, 30000),
      payrollTaxPct: safeNumber(payroll.payrollTaxPct, 25),
    },
  } as PayrollState;
}

export function reconstructOtherRevenue(
  otherRev: any,
): OtherRevenueState | undefined {
  if (!otherRev) return undefined;

  // Support both old 'wellness' and new 'spa' naming
  const spaData = otherRev.spa || otherRev.wellness;

  return {
    spa: spaData
      ? {
          avgPricePerTreatment: safeNumber(
            spaData.avgPricePerTreatment || spaData.avgPrice,
            88,
          ),
          treatmentsPerDay: safeNumber(spaData.treatmentsPerDay, 14),
        }
      : {
          avgPricePerTreatment: 0,
          treatmentsPerDay: 0,
        },
    other: otherRev.other
      ? {
          mode: otherRev.other.mode === "fixed" ? "fixed" : "percentage",
          percentageOfRooms: safeNumber(
            otherRev.other.percentageOfRooms || otherRev.other.value,
            69,
          ),
          monthlyFixed: safeNumber(otherRev.other.monthlyFixed, 0),
        }
      : {
          mode: "percentage",
          percentageOfRooms: 0,
          monthlyFixed: 0,
        },
  } as OtherRevenueState;
}

export function reconstructAssumptions(kpis: any): any {
  const assumptions: any = {};

  // Ramp settings
  if (kpis.ramp) {
    assumptions.rampSettings = {
      rampMonths: safeNumber(kpis.ramp.rampMonths, 12),
      openingMonth: safeNumber(kpis.ramp.openingMonth, 1),
      inflationRate: safeNumber(kpis.ramp.inflationRate, 2),
      revenueGrowthY2: safeNumber(kpis.ramp.revenueGrowthY2, 3),
      revenueGrowthY3Plus: safeNumber(kpis.ramp.revenueGrowthY3Plus, 2),
      stabilizedOccupancy: safeNumber(kpis.ramp.stabilizedOccupancy, 70),
    };
  }

  // Financing settings
  if (kpis.financing) {
    assumptions.financingSettings = {
      dscrTarget: safeNumber(kpis.financing.dscrTarget, 1.25),
      exitCapRate: safeNumber(kpis.financing.exitCapRate, 7),
      interestRate: safeNumber(kpis.financing.interestRate, 5),
      loanTermYears: safeNumber(kpis.financing.loanTermYears, 20),
      debtToEquityRatio: safeNumber(kpis.financing.debtToEquityRatio, 60),
    };
  }

  // Exit settings
  if (kpis.exit) {
    assumptions.exitSettings = {
      exitCapRate: safeNumber(kpis.exit.exitCapRate, 7),
      exitNOIMultiple: safeNumber(kpis.exit.exitNOIMultiple, 12),
      holdPeriodYears: safeNumber(kpis.exit.holdPeriodYears, 5),
      sellingCostsPct: safeNumber(kpis.exit.sellingCostsPct, 2),
    };
  }

  return Object.keys(assumptions).length > 0 ? assumptions : undefined;
}

export default {
  // Room Revenue
  reconstructRoomRevenueModel,
  hasValidRoomRevenue,

  // F&B
  reconstructFnBRevenue,

  // Budget
  reconstructBudget,

  // OPEX
  reconstructOpex,

  // Payroll
  reconstructPayroll,

  // Other Revenue
  reconstructOtherRevenue,

  // Assumptions
  reconstructAssumptions,

  safeNumber,
};
